/**
 * 指标卡片组件
 * 用于显示关键指标数值，支持趋势显示和告警状态
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  format?: 'number' | 'percentage' | 'currency' | 'duration' | 'custom';
  precision?: number;
  trend?: {
    direction: 'up' | 'down' | 'stable';
    value: number;
    label: string;
  };
  alert?: {
    level: 'info' | 'warning' | 'critical';
    message: string;
  };
  icon?: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  className?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  format = 'number',
  precision = 1,
  trend,
  alert,
  icon: Icon,
  loading = false,
  className
}) => {
  // 格式化数值
  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'percentage':
        return `${(val * 100).toFixed(precision)}%`;
      case 'currency':
        return `¥${val.toLocaleString('zh-CN')}`;
      case 'duration':
        return val < 1000 ? `${val}ms` : `${(val / 1000).toFixed(precision)}s`;
      case 'number':
        return val.toLocaleString('zh-CN');
      default:
        return val.toString();
    }
  };

  // 获取趋势图标和颜色
  const getTrendDisplay = () => {
    if (!trend) return null;

    const iconClass = 'h-4 w-4';
    const isPositiveTrend = trend.direction === 'up';
    
    return (
      <div className={clsx(
        'flex items-center text-xs font-medium',
        isPositiveTrend ? 'text-green-600' : trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
      )}>
        {trend.direction === 'up' && <TrendingUp className={iconClass} />}
        {trend.direction === 'down' && <TrendingDown className={iconClass} />}
        {trend.direction === 'stable' && <Minus className={iconClass} />}
        <span className="ml-1">
          {trend.direction !== 'stable' && (
            <span>{Math.abs(trend.value).toFixed(1)}% </span>
          )}
          {trend.label}
        </span>
      </div>
    );
  };

  // 获取告警样式
  const getAlertStyles = () => {
    if (!alert) return '';
    
    switch (alert.level) {
      case 'critical':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      case 'info':
        return 'border-blue-200 bg-blue-50';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className={clsx('card p-6', className)}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            {Icon && <div className="h-8 w-8 bg-gray-200 rounded"></div>}
          </div>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(
      'card p-6 relative',
      getAlertStyles(),
      className
    )}>
      {/* 告警图标 */}
      {alert && (
        <div className="absolute top-2 right-2">
          <AlertTriangle className={clsx(
            'h-4 w-4',
            alert.level === 'critical' ? 'text-red-500' : 
            alert.level === 'warning' ? 'text-yellow-500' : 'text-blue-500'
          )} />
        </div>
      )}

      {/* 头部 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {Icon && <Icon className="h-8 w-8 text-gray-400" />}
      </div>

      {/* 主要数值 */}
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-2xl font-semibold text-gray-900">
            {formatValue(value)}
          </p>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        
        {/* 趋势指示器 */}
        <div className="text-right">
          {getTrendDisplay()}
        </div>
      </div>

      {/* 告警消息 */}
      {alert && (
        <div className="mt-3 text-xs text-gray-600">
          {alert.message}
        </div>
      )}
    </div>
  );
};

export default MetricCard;